(function () {
  if (window.__searchTypewriterInit) {
    return;
  }
  window.__searchTypewriterInit = true;

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  // PM-PERF (21-oct-2026): la animación no parte durante la carga. Espera
  // a "load" y a que el hilo principal quede libre (requestIdleCallback,
  // timeout 2 s), así no compite con los toques tempranos del usuario
  // (INP). Además, cada input solo anima mientras está en pantalla: el
  // buscador de escritorio y el móvil existen a la vez y uno siempre está
  // oculto.
  let pageReady = false;
  const readyCallbacks = [];

  function whenPageReady(callback) {
    if (pageReady) {
      callback();
    } else {
      readyCallbacks.push(callback);
    }
  }

  function markPageReady() {
    pageReady = true;
    readyCallbacks.splice(0).forEach((callback) => callback());
  }

  function schedulePageReady() {
    const go = () => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(markPageReady, { timeout: 2000 });
      } else {
        setTimeout(markPageReady, 1200);
      }
    };
    if (document.readyState === "complete") {
      go();
    } else {
      window.addEventListener("load", go, { once: true });
    }
  }

  schedulePageReady();

  class Typewriter {
    constructor(input) {
      this.input = input;
      this.fallbackPlaceholder = input.getAttribute("placeholder") || "";

      const raw = [input.dataset.typewriterPrimary, input.dataset.typewriterSecondary]
        .map((value) => (value || "").trim())
        .filter((value) => value.length > 0);

      this.phrases = raw.map((value) => Array.from(value));
      this.rawPhrases = raw;

      this.timeoutId = null;
      this.caretIntervalId = null;
      this.phraseIndex = 0;
      this.charIndex = 0;
      this.phase = "type";
      this.caretVisible = true;
      this.running = false;

      this.handleFocus = this.handleFocus.bind(this);
      this.handleBlur = this.handleBlur.bind(this);
      this.handleInputChange = this.handleInputChange.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    init() {
      if (!this.phrases.length) {
        return;
      }

      if (prefersReducedMotion.matches) {
        this.input.setAttribute("placeholder", this.rawPhrases[0]);
        return;
      }

      this.input.addEventListener("focus", this.handleFocus);
      this.input.addEventListener("blur", this.handleBlur);
      this.input.addEventListener("input", this.handleInputChange);
      document.addEventListener("visibilitychange", this.handleVisibilityChange);

      this.inView = false;
      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[entries.length - 1].isIntersecting;
          if (this.inView) {
            this.maybeStart();
          } else {
            this.stop({ preservePlaceholder: true });
          }
        });
        this.observer.observe(this.input);
      } else {
        this.inView = true;
      }

      whenPageReady(() => this.maybeStart());
    }

    maybeStart() {
      if (!pageReady || !this.inView || document.hidden) {
        return;
      }
      if (document.activeElement === this.input || this.input.value) {
        return;
      }
      this.start();
    }

    handleFocus() {
      this.stop();
      this.input.setAttribute("placeholder", this.fallbackPlaceholder);
    }

    handleBlur() {
      if (this.input.value) {
        return;
      }
      this.reset();
      this.maybeStart();
    }

    handleInputChange() {
      if (this.input.value) {
        this.stop();
      }
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stop({ preservePlaceholder: true });
      } else {
        this.maybeStart();
      }
    }

    reset() {
      this.phraseIndex = 0;
      this.charIndex = 0;
      this.phase = "type";
    }

    start() {
      if (this.running || !this.phrases.length) {
        return;
      }
      this.running = true;
      this.startCaretBlink();
      this.tick();
    }

    stop({ preservePlaceholder = false } = {}) {
      this.running = false;
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      this.stopCaretBlink();
      if (!preservePlaceholder && this.input) {
        this.input.setAttribute("placeholder", this.fallbackPlaceholder);
      }
    }

    startCaretBlink() {
      this.caretVisible = true;
      this.stopCaretBlink();
      this.caretIntervalId = setInterval(() => {
        this.caretVisible = !this.caretVisible;
        this.render();
      }, 530);
    }

    stopCaretBlink() {
      if (this.caretIntervalId) {
        clearInterval(this.caretIntervalId);
        this.caretIntervalId = null;
      }
    }

    render() {
      if (!this.input || !this.phrases.length) {
        return;
      }
      const phrase = this.phrases[this.phraseIndex];
      const visible = phrase.slice(0, this.charIndex).join("");
      const showCaret = this.phase !== "delete" && this.caretVisible;
      this.input.setAttribute("placeholder", showCaret ? `${visible}│` : visible);
    }

    tick() {
      if (!this.running) {
        return;
      }

      const phrase = this.phrases[this.phraseIndex];

      if (this.phase === "type") {
        this.charIndex += 1;
        this.render();
        if (this.charIndex >= phrase.length) {
          this.phase = "hold";
          this.schedule(1500);
        } else {
          this.schedule(70 + Math.random() * 40 - 20);
        }
        return;
      }

      if (this.phase === "hold") {
        this.phase = "delete";
        this.schedule(40);
        return;
      }

      if (this.phase === "delete") {
        this.charIndex = Math.max(0, this.charIndex - 1);
        this.render();
        if (this.charIndex === 0) {
          this.phase = "type";
          this.phraseIndex = (this.phraseIndex + 1) % this.phrases.length;
          this.schedule(400);
        } else {
          this.schedule(40);
        }
      }
    }

    schedule(delay) {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      this.timeoutId = setTimeout(() => this.tick(), delay);
    }
  }

  function initAll(root) {
    const scope = root || document;
    const inputs = scope.querySelectorAll(
      "input[data-typewriter-primary]:not([data-typewriter-initialized])",
    );
    inputs.forEach((input) => {
      input.setAttribute("data-typewriter-initialized", "true");
      new Typewriter(input).init();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  window.addEventListener("load", () => initAll());

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
